
define([
    "dojo/_base/declare",
    "dojo/_base/lang",
    "dojo/dom-construct",
    "mw-deferred-utils/Deferred",
    "mw-deferred-utils/DeferredList",
    "mw-log/Log"
], function (declare, lang, domConstruct, Deferred, DeferredList, Log) {

    /**
     * This class deals with updating ImageData based properties of peer nodes. It can apply
     * partial updates and full updates to a peer node property. It manages the asynchronous loading
     * of images sent from the server and makes sure they are applied to the image in the correct
     * order.
     */
    return declare([], {
        _updateQueue: {},

        constructor: function (originator) {
            this._canvas = domConstruct.create("canvas");
            this._context = this._canvas.getContext("2d");
            this._originator = originator;
        },

        computeImageDataDifferences: function (oldImageData, newImageData) {
            return [{x: 0, y: 0, image: newImageData}];
        },

        applyImageDifferences: function (node, key, diffImages) {
            var i, len = diffImages.length;
            var that = this;

            if (len === 0) {
                return;
            }

            var deferred = this._createQueuedDeferred(node, key);
            deferred.name = "applyImageDifferences deferred";

            // create deferred wrappers for the images
            var loadingDeferreds = [];
            for (i = 0; i < len; i += 1) {
                loadingDeferreds.push(this._wrapImageInDeferred(diffImages[i].image));
            }
            var loadingList = new DeferredList(loadingDeferreds, false, true, false);

            var previousDeferred = that._updateQueue[node.getId() + key][that._updateQueue[
                node.getId() + key].length - 2];

            // when they are all loaded, apply the image difference
            loadingList.then(function () {
                // wait for the last queued image diff to be applied
                if (previousDeferred) {
                    previousDeferred.then(function () {
                        that._applyLoadedImageDifferences(node, key, diffImages, deferred);
                    });
                } else {
                    that._applyLoadedImageDifferences(node, key, diffImages, deferred);
                }
            }, function () {
                Log.log("rejecting deferred applyImageDifferences");
                // if one of the images can't be loaded, reject this deferred and cancel all others
                deferred.reject();
                that._cancelQueuedDeferreds(node, key);
            });

            return deferred;
        },

        updatePropertyWithImage: function (node, key, image) {
            var imageLoad = this._wrapImageInDeferred(image);
            var updateDeferred = this._createQueuedDeferred(node, key);
            updateDeferred.name = "updateImage deferred";
            var that = this;

            imageLoad.then(function () {

                that._canvas.width = image.width;
                that._canvas.height = image.height;

                // convert from Image to ImageData using Canvas
                that._context.drawImage(image, 0, 0, image.width, image.height);
                var imageData = that._context.getImageData(
                    0, 0, that._canvas.width, that._canvas.height);
                node.setProperty(key, imageData, that._originator);

                // clear out any pending incremental updates in the queue from before this deferred,
                // because this supersedes them.
                that._cancelQueuedDeferreds(node, key, updateDeferred);

                // trigger the next deferred in the queue
                if (!updateDeferred.isCanceled()) {
                    updateDeferred.resolve();
                }

            }, function () {
                if (!updateDeferred.isCanceled()) {
                    updateDeferred.reject();
                }
            });
            return updateDeferred;
        },


        // update the property to a new ImageData object
        _applyLoadedImageDifferences: function (node, key, diffImages, deferred) {
            var oldImageData = node.getProperty(key);
            this._canvas.width = oldImageData.width;
            this._canvas.height = oldImageData.height;

            // draw the original image which will be updated in the loop
            this._context.putImageData(oldImageData, 0, 0);

            var i;
            for (i = 0; i < diffImages.length; i += 1) {
                // clear the rectangle first in case the image is transparent.
                this._context.beginPath();
                this._context.clearRect(diffImages[i].x, diffImages[i].y,
                    diffImages[i].image.width, diffImages[i].image.height);
                this._context.closePath();
                this._context.drawImage(diffImages[i].image, diffImages[i].x, diffImages[i].y);
            }

            // convert to a data url and then convert that to an Image and wait for it to load. even
            // though it is already "loaded", the browser can take some time to decode the png.
            var imageData = this._context.getImageData(
                0, 0, this._canvas.width, this._canvas.height);
            node.setProperty(key, imageData, this._originator);

            deferred.resolve();
        },

        // wrap the loading of an image in a deferred so that it can be treated in a uniform manner.
        _wrapImageInDeferred: function (image) {
            var deferred = new Deferred();
            deferred.name = "wrapImage deferred";

            var onloadHandler = function () {
                // use setTimeout because IE9 canvas fails to render the image if you try to
                // drawImage in the same JS callback frame. Delaying it seems to workaround the
                // issue.
                setTimeout(function () {
                    if (image.onload) {
                        deferred.resolve();
                        image.onload = null;
                        image.onerror = null;
                    }
                }, 0);
            };
            var onerrorHandler = function () {
                Log.log("Error loading image");
                deferred.reject();
                image.onload = null;
                image.onerror = null;
            };

            image.onload = onloadHandler;
            image.onerror = onerrorHandler;

            if (image.complete && image.src.length > 0) {
                onloadHandler();
            }
            return deferred;
        },

        _createQueuedDeferred: function (node, key) {
            var deferred;

            deferred = new Deferred();
            var cleanup = lang.hitch(this, this._removeDeferredFromQueue, node, key, deferred);

            deferred.then(cleanup, cleanup);
            this._updateQueue[node.getId() + key] = this._updateQueue[node.getId() + key] || [];
            this._updateQueue[node.getId() + key].push(deferred);

            return deferred;
        },

        _removeDeferredFromQueue: function (node, key, deferred) {
            if (this._updateQueue[node.getId() + key] === undefined) {
                delete this._updateQueue[node.getId() + key];
                return;
            }
            var i = this._updateQueue[node.getId() + key].indexOf(deferred);
            if (i >= 0) {
                this._updateQueue[node.getId() + key].splice(i, 1);
            }
            if (this._updateQueue[node.getId() + key].length === 0) {
                this._updateQueue[node.getId() + key] = undefined;
                delete this._updateQueue[node.getId() + key];
            }
        },

        _cancelQueuedDeferreds: function (node, key, deferred) {
            var queue = this._updateQueue[node.getId() + key];
            while (queue && queue.length > 0) {
                var oldDeferred = queue.shift();
                if (deferred && oldDeferred === deferred) {
                    return;
                }
                oldDeferred.cancel("Queued deferreds cancelled");
            }
            this._updateQueue[node.getId() + key] = undefined;
            delete this._updateQueue[node.getId() + key];
        }
    });
});

